import { Component, Input, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { TimelineEvent } from '../../order-v2.types';
import { FeatherPipe } from '../../../../../core/pipes/feather.pipe';

type TimelineTab = 'consolidated' | 'kruzer';

@Component({
  selector: 'app-order-v2-timeline',
  standalone: true,
  imports: [CommonModule, FeatherPipe],
  templateUrl: './order-timeline.component.html',
  styleUrl: './order-timeline.component.scss',
})
export class OrderTimelineComponent {
  @Input({ required: true }) events: TimelineEvent[] = [];
  @Input() kruzerEvents: TimelineEvent[] = [];
  @Input() loading = false;

  private readonly sanitizer = inject(DomSanitizer);

  readonly activeTab = signal<TimelineTab>('consolidated');
  readonly copiedKey = signal<string | null>(null);
  private readonly expandedKeys = signal<ReadonlySet<string>>(new Set());
  private copiedTimer: ReturnType<typeof setTimeout> | null = null;

  get eventsCount(): number {
    return this.activeTab() === 'consolidated' ? this.events.length : this.kruzerEvents.length;
  }

  get visibleEvents(): TimelineEvent[] {
    return this.activeTab() === 'consolidated' ? this.events : this.kruzerEvents;
  }

  selectTab(tab: TimelineTab): void {
    this.activeTab.set(tab);
    this.expandedKeys.set(new Set());
  }

  dateOf(event: TimelineEvent): string {
    const [date] = (event.occurredAt ?? '').split(' ');
    return date ?? '';
  }

  timeOf(event: TimelineEvent): string {
    const parts = (event.occurredAt ?? '').split(' ');
    return parts[1] ?? '';
  }

  shouldShowDate(index: number): boolean {
    if (index === 0) return true;
    const events = this.visibleEvents;
    return this.dateOf(events[index]) !== this.dateOf(events[index - 1]);
  }

  hasPayload(event: TimelineEvent): boolean {
    return event.payload !== null && event.payload !== undefined;
  }

  isExpanded(index: number): boolean {
    return this.expandedKeys().has(this.keyFor(index));
  }

  toggleEvent(event: TimelineEvent, index: number): void {
    if (!this.hasPayload(event)) return;
    const key = this.keyFor(index);
    const next = new Set(this.expandedKeys());
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    this.expandedKeys.set(next);
  }

  copyPayload(event: TimelineEvent, index: number): void {
    const text = this.formatPayload(event.payload);
    if (!text) return;
    const key = this.keyFor(index);

    const showCopied = () => {
      if (this.copiedTimer) clearTimeout(this.copiedTimer);
      this.copiedKey.set(key);
      this.copiedTimer = setTimeout(() => this.copiedKey.set(null), 1500);
    };

    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(showCopied).catch(() => this.fallbackCopy(text, showCopied));
    } else {
      this.fallbackCopy(text, showCopied);
    }
  }

  isCopied(index: number): boolean {
    return this.copiedKey() === this.keyFor(index);
  }

  private fallbackCopy(text: string, onSuccess: () => void): void {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    try {
      document.execCommand('copy');
      onSuccess();
    } finally {
      document.body.removeChild(textarea);
    }
  }

  formatPayload(payload: unknown): string {
    if (payload === null || payload === undefined) return '';
    if (Array.isArray(payload) && payload.every((p) => typeof p === 'string')) {
      return (payload as string[]).join('\n');
    }
    if (typeof payload === 'string') {
      try {
        return JSON.stringify(JSON.parse(payload), null, 2);
      } catch {
        return payload;
      }
    }
    try {
      return JSON.stringify(payload, null, 2);
    } catch {
      return String(payload);
    }
  }

  isDfdlPayload(payload: unknown): boolean {
    return Array.isArray(payload) && payload.every((p) => typeof p === 'string');
  }

  dfdlLines(payload: unknown): string[] {
    return this.isDfdlPayload(payload) ? (payload as string[]) : [];
  }

  isJsonPayload(payload: unknown): boolean {
    if (payload === null || payload === undefined) return false;
    if (Array.isArray(payload) && payload.every((p) => typeof p === 'string')) return false;
    if (typeof payload === 'string') {
      try {
        JSON.parse(payload);
        return true;
      } catch {
        return false;
      }
    }
    return typeof payload === 'object' || Array.isArray(payload);
  }

  formatPayloadHtml(payload: unknown): SafeHtml {
    const text = this.formatPayload(payload);
    const escaped = text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

    const highlighted = escaped.replace(
      /("(?:\\u[a-fA-F0-9]{4}|\\[^u]|[^"\\])*")(\s*:)?|\b(true|false)\b|\bnull\b|-?\b\d+(?:\.\d+)?(?:[eE][+-]?\d+)?\b/g,
      (match, str, colon, bool) => {
        if (str !== undefined) {
          if (colon !== undefined) {
            return `<span class="json-key">${str}</span>${colon}`;
          }
          return `<span class="json-string">${str}</span>`;
        }
        if (bool !== undefined) {
          return `<span class="json-boolean">${bool}</span>`;
        }
        if (match === 'null') {
          return `<span class="json-null">${match}</span>`;
        }
        return `<span class="json-number">${match}</span>`;
      },
    );
    return this.sanitizer.bypassSecurityTrustHtml(highlighted);
  }

  private keyFor(index: number): string {
    return `${this.activeTab()}:${index}`;
  }
}
